'use client';

import { ReactNode, useRef } from 'react';

import Image from 'next/image';

import Autoplay from 'embla-carousel-autoplay';

import { Carousel, CarouselContent, CarouselItem } from '@kit/ui/carousel';

import { LandingIcons } from '../icons';
import BlurFade from '../magicui/blur-fade';
import Section from '../section';
import GlowingBadge from '../ui/glowing-badge';
import { BackgroundParticles } from './new-hero/background/background-particles';
import { BackgroundSquares } from './new-hero/background/background-squares';

type TestimonialProps = {
  content?: ReactNode;
  avatar?: string;
  name?: string;
  title?: string;
  companyLogo?: string;
  companyName?: string;
};

export default function Component({
  testimonials,
}: {
  testimonials: TestimonialProps[];
}) {
  const autoplayPlugin = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true }),
  );

  return (
    <div className="relative overflow-hidden">
      <Image
        src="/images/testimonial-carousel-bg.png"
        alt="Testimonials Background"
        className="pointer-events-none absolute left-1/2 top-1/2 max-w-none -translate-x-1/2 -translate-y-1/2 transform"
        width={3010}
        height={607}
      />
      <div className="pointer-events-none absolute top-0 z-10 h-full w-full">
        <BackgroundParticles />
      </div>
      <BackgroundSquares className="pointer-events-none absolute top-0 z-10 !h-full w-full" />
      <Section
        id="testimonials-carousel"
        className="relative z-10 px-4 lg:px-8 lg:pb-3 lg:pt-9"
      >
        <BlurFade inView>
          <div className="mb-5 flex justify-center lg:mb-16">
            <GlowingBadge>
              <LandingIcons.testimonial />
              <span>Testimonials</span>
            </GlowingBadge>
          </div>
        </BlurFade>

        <Carousel plugins={[autoplayPlugin.current]} opts={{ loop: true }}>
          <div className="relative mx-auto">
            <CarouselContent>
              {testimonials.map(
                (
                  { content, avatar, name, title, companyLogo, companyName },
                  index,
                ) => (
                  <CarouselItem key={index}>
                    <div className="p-2 pb-0 lg:pb-5">
                      <BlurFade delay={0.25} inView>
                        <LandingIcons.quote className="mx-auto mt-2 w-[85px] lg:mb-2 lg:w-[104px]" />
                        <h4 className="mx-auto max-w-[1060px] text-center text-2xl leading-[1.5] tracking-[-0.48px] text-white lg:text-[32px] lg:tracking-[-0.74px]">
                          {content}
                        </h4>
                      </BlurFade>
                      <div className="mx-auto mt-11 flex items-center md:justify-center lg:mt-10">
                        <BlurFade delay={0.25 * 5} inView>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          {avatar ? (
                            <Image
                              src={avatar}
                              alt={name ? `${name} avatar` : 'User avatar'}
                              width={56}
                              height={56}
                              className="h-12 w-12 rounded-full lg:h-14 lg:w-14"
                            />
                          ) : null}
                        </BlurFade>
                        <div className="ml-3 mr-2 flex-1 text-left md:flex-initial lg:ml-6 lg:mr-8">
                          <BlurFade delay={0.25 * 3} inView>
                            <h4 className="pr-14 font-medium leading-6 tracking-[0.16px] text-white lg:text-xl lg:leading-normal lg:tracking-normal">
                              {name}
                            </h4>
                          </BlurFade>
                          <BlurFade delay={0.25 * 4} inView>
                            <span className="text-sm text-white/60">
                              {title}
                            </span>
                          </BlurFade>
                        </div>

                        {companyLogo && companyName ? (
                          <BlurFade
                            delay={0.25 * 2}
                            inView
                            className="flex h-7 w-18 items-center justify-end lg:h-10 lg:w-28 lg:justify-normal"
                          >
                            <Image
                              width={94}
                              height={40}
                              key={index}
                              src={companyLogo}
                              alt={companyName}
                              className="opacity-30 brightness-0 grayscale invert"
                            />
                          </BlurFade>
                        ) : null}
                      </div>
                    </div>
                  </CarouselItem>
                ),
              )}
            </CarouselContent>
          </div>
        </Carousel>
      </Section>
    </div>
  );
}
